import {
    Circle,
    Ellipse,
    FabricObject,
    Group,
    Line,
    Path,
    Polygon,
    Polyline,
    Rect
} from 'fabric';
import {
    Download,
    ExternalLink,
    File,
    FileArchive,
    FileAudio,
    FileImage,
    FileSpreadsheet,
    FileVideo,
    Link
} from 'lucide';
import {customIconNodes} from '../shared/customIcons.ts';

/*
 * Icons for the resource card. React components cannot be drawn inside a
 * canvas, and loadSVGFromString is an asynchronous detour for a handful of
 * shapes. Instead the icon's element list is taken straight from Lucide's
 * plain icon data (the `lucide` package, or customIconNodes for the ones
 * Lucide lacks) and rebuilt as fabric shapes, which is synchronous and needs
 * no network.
 */

// An icon's element list, as both the lucide and lucide-react packages
// describe it: [tag, attributes] pairs in a 24x24 coordinate space.
export type GlyphIcon = readonly (readonly [string, object, ...unknown[]])[];

export interface GlyphSpec {
    icon: GlyphIcon;
    accent: string;
}

export const LinkGlyph: GlyphSpec = {icon: Link, accent: '#2F9E68'};
export const DefaultFileGlyph: GlyphSpec = {icon: File, accent: '#5C7CFA'};

const FileGlyphs: {extensions: string[]; spec: GlyphSpec}[] = [
    {
        extensions: ['pdf'],
        spec: {icon: customIconNodes.filePdf, accent: '#E2574C'}
    },
    {
        extensions: ['doc', 'docx', 'rtf', 'odt'],
        spec: {icon: customIconNodes.fileWord, accent: '#2B579A'}
    },
    {
        extensions: ['ppt', 'pptx', 'odp'],
        spec: {icon: customIconNodes.filePowerpoint, accent: '#D24726'}
    },
    {
        extensions: ['xls', 'xlsx', 'csv', 'ods'],
        spec: {icon: FileSpreadsheet, accent: '#217346'}
    },
    {
        extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'],
        spec: {icon: FileImage, accent: '#7E57C2'}
    },
    {
        extensions: ['mp3', 'wav', 'ogg', 'm4a'],
        spec: {icon: FileAudio, accent: '#00897B'}
    },
    {
        extensions: ['mp4', 'mov', 'webm', 'avi'],
        spec: {icon: FileVideo, accent: '#5E35B1'}
    },
    {
        extensions: ['zip', 'rar', '7z', 'tar', 'gz'],
        spec: {icon: FileArchive, accent: '#6D4C41'}
    }
];

/*
 * Picks the glyph and accent colour for a file extension. Anything
 * unrecognised gets the generic file glyph rather than no icon at all.
 */
export function glyphSpecForExtension(extension?: string | null): GlyphSpec {
    const normalized = (extension ?? '').toLowerCase().replace(/^\./, '');

    if (!normalized) {
        return DefaultFileGlyph;
    }

    const match = FileGlyphs.find(entry =>
        entry.extensions.includes(normalized)
    );

    return match ? match.spec : DefaultFileGlyph;
}

export const ActionGlyphs = {
    download: Download,
    open: ExternalLink
};

const num = (value: unknown) => Number(value ?? 0);

const points = (value: unknown) =>
    String(value ?? '')
        .trim()
        .split(/[\s,]+/)
        .map(Number)
        .reduce<{x: number; y: number}[]>((acc, n, i, all) => {
            if (i % 2 === 0) acc.push({x: n, y: all[i + 1]});
            return acc;
        }, []);

/*
 * Builds one fabric shape for a Lucide element, drawn in the icon's own
 * 24x24 coordinate space with Lucide's stroke defaults. 'currentColor'
 * becomes the requested colour.
 */
function makeShape(tag: string, attrs: Record<string, unknown>, color: string) {
    const paint = (value: unknown, fallback: string | null) =>
        value === undefined
            ? fallback
            : value === 'none'
            ? null
            : value === 'currentColor'
            ? color
            : String(value);

    const style = {
        fill: paint(attrs.fill, null),
        stroke: paint(attrs.stroke, color),
        strokeWidth: num(attrs.strokeWidth ?? attrs['stroke-width'] ?? 2),
        strokeLineCap: 'round' as const,
        strokeLineJoin: 'round' as const
    };

    switch (tag) {
        case 'path':
            return new Path(String(attrs.d), style);
        case 'circle': {
            const r = num(attrs.r);
            return new Circle({
                ...style,
                radius: r,
                left: num(attrs.cx) - r,
                top: num(attrs.cy) - r
            });
        }
        case 'ellipse': {
            const rx = num(attrs.rx);
            const ry = num(attrs.ry);
            return new Ellipse({
                ...style,
                rx,
                ry,
                left: num(attrs.cx) - rx,
                top: num(attrs.cy) - ry
            });
        }
        case 'rect':
            return new Rect({
                ...style,
                left: num(attrs.x),
                top: num(attrs.y),
                width: num(attrs.width),
                height: num(attrs.height),
                rx: num(attrs.rx),
                ry: num(attrs.ry ?? attrs.rx)
            });
        case 'line':
            return new Line(
                [num(attrs.x1), num(attrs.y1), num(attrs.x2), num(attrs.y2)],
                style
            );
        case 'polyline':
            return new Polyline(points(attrs.points), style);
        case 'polygon':
            return new Polygon(points(attrs.points), style);
        default:
            return null;
    }
}

/*
 * Builds a fabric Group from a Lucide icon, scaled so its longest side is
 * `size` and centred on (centerX, centerY) in the parent's coordinate space.
 * The glyph is never a click target of its own: clicks must land on the card.
 */
export function makeGlyph(
    icon: GlyphIcon,
    size: number,
    color: string,
    centerX: number,
    centerY: number
): Group {
    const shapes = icon
        .map(([tag, attrs]) =>
            makeShape(tag, attrs as Record<string, unknown>, color)
        )
        .filter(
            (shape): shape is NonNullable<typeof shape> => shape !== null
        ) as FabricObject[];

    const glyph = new Group(shapes, {
        selectable: false,
        evented: false
    });

    const scale = size / Math.max(glyph.width || 1, glyph.height || 1);

    glyph.set({
        scaleX: scale,
        scaleY: scale,
        left: centerX - (glyph.width * scale) / 2,
        top: centerY - (glyph.height * scale) / 2
    });

    return glyph;
}
